import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CaretDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  PackageIcon,
  FileTextIcon,
} from '@phosphor-icons/react';
import type { InvoiceAudit } from 'src/api/rebates/types/auditTypes';
import { formatCurrency } from '@erp/shared-utils';

interface InvoiceGroupedViewProps {
  invoices: InvoiceAudit[];
}

export function InvoiceGroupedView({ invoices }: InvoiceGroupedViewProps) {
  // Start with all invoices expanded
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(
    new Set(invoices.map((inv) => inv.invoiceId)),
  );

  const toggleInvoice = (invoiceId: string) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);
    }
    setExpandedInvoices(newExpanded);
  };

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No invoices found for this agreement period</p>
      </div>
    );
  }

  // Sort invoices by date (newest first)
  const sortedInvoices = [...invoices].sort(
    (a, b) =>
      new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime(),
  );

  // Calculate division-level summary
  const divisionSummary = invoices.reduce(
    (acc, invoice) => {
      if (!acc[invoice.divisionId]) {
        acc[invoice.divisionId] = {
          divisionId: invoice.divisionId,
          divisionName: invoice.divisionName,
          invoiceCount: 0,
          totalValue: 0,
          totalRebateEarned: 0,
        };
      }
      acc[invoice.divisionId].invoiceCount++;
      acc[invoice.divisionId].totalValue += invoice.totalValue;
      acc[invoice.divisionId].totalRebateEarned += invoice.totalRebateEarned;
      return acc;
    },
    {} as Record<
      string,
      {
        divisionId: string;
        divisionName: string;
        invoiceCount: number;
        totalValue: number;
        totalRebateEarned: number;
      }
    >,
  );

  const divisionSummaryArray = Object.values(divisionSummary);

  return (
    <div className="space-y-6">
      {/* Division Summary Section */}
      {divisionSummaryArray.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Division Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Division</th>
                    <th className="text-right p-3 font-medium">Invoices</th>
                    <th className="text-right p-3 font-medium">Total Value</th>
                    <th className="text-right p-3 font-medium">
                      Total Rebate Earned
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {divisionSummaryArray.map((div) => (
                    <tr key={div.divisionId} className="hover:bg-muted/30">
                      <td className="p-3">{div.divisionName}</td>
                      <td className="p-3 text-right">{div.invoiceCount}</td>
                      <td className="p-3 text-right">
                        {formatCurrency(div.totalValue)}
                      </td>
                      <td className="p-3 text-right font-medium text-green-600">
                        {formatCurrency(div.totalRebateEarned)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Individual Invoices */}
      {sortedInvoices.map((invoice) => {
        const isExpanded = expandedInvoices.has(invoice.invoiceId);

        return (
          <Card key={invoice.invoiceId} className="overflow-hidden">
            <Collapsible
              open={isExpanded}
              onOpenChange={() => toggleInvoice(invoice.invoiceId)}
            >
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <FileTextIcon className="h-5 w-5 text-muted-foreground" />
                        <CardTitle className="text-lg">
                          {new Date(invoice.invoiceDate).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            },
                          )}
                        </CardTitle>
                        <Badge variant="outline">{invoice.divisionName}</Badge>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Total Value</p>
                          <p className="font-medium">
                            {formatCurrency(invoice.totalValue)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Rebate Earned</p>
                          <p className="font-semibold text-lg text-green-600">
                            {formatCurrency(invoice.totalRebateEarned)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Line Items</p>
                          <p className="font-medium">{invoice.lineItemCount}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">
                            Rules Qualified
                          </p>
                          <p className="font-medium">
                            {invoice.rulesQualified}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">
                            Rules Affected
                          </p>
                          <p className="font-medium">{invoice.rulesAffected}</p>
                        </div>
                      </div>
                    </div>

                    <CaretDownIcon
                      className={`h-5 w-5 text-muted-foreground transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
                  {/* Rule Evaluations */}
                  {invoice.ruleEvaluations.length > 0 ? (
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">
                        Rule Evaluations ({invoice.ruleEvaluations.length})
                      </h4>
                      <div className="space-y-2">
                        {invoice.ruleEvaluations.map((evaluation) => {
                          const hasRebate = evaluation.rebateEarned > 0;

                          return (
                            <Card
                              key={evaluation.ruleId}
                              className={`border ${
                                hasRebate
                                  ? 'border-green-200 bg-green-50/30'
                                  : 'border-gray-200'
                              }`}
                            >
                              <CardContent className="p-4">
                                <div className="space-y-3">
                                  {/* Rule Header */}
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <p className="font-medium">
                                          {evaluation.ruleName}
                                        </p>
                                        <Badge
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          Order {evaluation.ruleOrder}
                                        </Badge>
                                      </div>
                                    </div>
                                    {hasRebate && (
                                      <div className="text-right">
                                        <p className="text-xs text-muted-foreground">
                                          Rebate Earned
                                        </p>
                                        <p className="font-semibold text-green-600">
                                          {formatCurrency(
                                            evaluation.rebateEarned,
                                          )}
                                        </p>
                                      </div>
                                    )}
                                  </div>

                                  {/* Qualification Status */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Qualifying */}
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        {evaluation.qualified ? (
                                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <XCircleIcon className="h-4 w-4 text-gray-400" />
                                        )}
                                        <span className="text-sm font-medium">
                                          Qualifying
                                        </span>
                                      </div>
                                      <p className="text-xs text-muted-foreground pl-6">
                                        {evaluation.qualifyingReason}
                                      </p>
                                      {evaluation.qualifyingVolume > 0 && (
                                        <p className="text-sm pl-6">
                                          Volume:{' '}
                                          {formatCurrency(
                                            evaluation.qualifyingVolume,
                                          )}
                                        </p>
                                      )}
                                    </div>

                                    {/* Affected */}
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        {evaluation.affected ? (
                                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                                        ) : (
                                          <XCircleIcon className="h-4 w-4 text-gray-400" />
                                        )}
                                        <span className="text-sm font-medium">
                                          Affected
                                        </span>
                                      </div>
                                      <p className="text-xs text-muted-foreground pl-6">
                                        {evaluation.affectedReason}
                                      </p>
                                      {evaluation.affectedVolume > 0 && (
                                        <p className="text-sm pl-6">
                                          Volume:{' '}
                                          {formatCurrency(
                                            evaluation.affectedVolume,
                                          )}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Product Group Details */}
                                  {evaluation.productGroupEvaluation && (
                                    <div className="pt-3 border-t">
                                      <div className="flex items-center gap-2 mb-2">
                                        <PackageIcon className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-xs font-medium text-muted-foreground">
                                          Product Groups
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                        <div>
                                          <p className="text-muted-foreground mb-1">
                                            Invoice Groups:
                                          </p>
                                          <div className="flex flex-wrap gap-1">
                                            {evaluation.productGroupEvaluation.invoiceProductGroups.map(
                                              (group) => (
                                                <Badge
                                                  key={group}
                                                  variant="secondary"
                                                  className="text-xs"
                                                >
                                                  {group}
                                                </Badge>
                                              ),
                                            )}
                                          </div>
                                        </div>
                                        <div>
                                          <p className="text-muted-foreground mb-1">
                                            Rule Groups (
                                            {
                                              evaluation.productGroupEvaluation
                                                .qualifyingInclusionType
                                            }
                                            ):
                                          </p>
                                          <div className="flex flex-wrap gap-1">
                                            {evaluation.productGroupEvaluation.qualifyingProductGroups.map(
                                              (group) => (
                                                <Badge
                                                  key={group}
                                                  variant="outline"
                                                  className="text-xs"
                                                >
                                                  {group}
                                                </Badge>
                                              ),
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No rule evaluations for this invoice
                    </p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
}
